import { GAMEPLAY } from '@/config/gameplay';
import type {
  CellId,
  DetectedLoop,
  LoopDiagnostics,
  RejectedLoopDiagnostic,
} from '@/types';
import { LoopTooLargeError, pruneDeadEnds } from './loops';
import {
  buildLoopInterior,
  loopInteriorCellCount,
  loopInteriorHas,
  hasSolidCore,
  loopInteriorOverlapCount,
  newGroundOutside,
  shrunkInteriorCellCount,
} from './loopInterior';
import { ringOf } from './neighbours';

interface AcceptedLoopRecord {
  loop: DetectedLoop;
  /** Az a traversal-index, ahol ezt a geometriai hurkot jóváírtuk. */
  toIndex: number;
}

interface ClosureBlock {
  /** Az éppen lezárt régió. Amíg benne/peremén haladunk, nincs új kompozit closure. */
  loop: DetectedLoop;
  acceptedAt: number;
}

/**
 * Egyetlen H3 kontaktfolt több egymás melletti korábbi cellát is érinthet.
 * Ezek a pathon jellemzően néhány indexen belül vannak, de geometriában ugyanazt
 * a kereszteződést jelentik. Ha mindegyiket külön jelöltként futtatjuk, ugyanarra
 * a fizikai találkozásra 2–6× Tarjan + flood fill indul.
 *
 * A 6 index elég tág egy res12 kontaktfolthoz, de több külön kör / külön korábbi
 * áthaladás tipikusan nagyságrendekkel távolabb van a pathon, tehát azok nem
 * olvadnak össze.
 */
const CONTACT_INDEX_CLUSTER_GAP = 6;

/**
 * Ismételt, tényleges új traversalnál ekkora H3-kvantálási eltérést tekintünk
 * ugyanannak a fizikai huroknak. Ez nem deduplikálja a bezárást: a defense nő,
 * csak nem engedi, hogy ugyanaz a kör 1-2 kapucella jitter miatt terjeszkedjen.
 */
const REPEAT_CANONICAL_MAX_SYMMETRIC_DIFF = 2;

export interface IncrementalLoopSnapshot {
  loops: DetectedLoop[];
  diagnostics: LoopDiagnostics;
}

/**
 * Állapottartó, inkrementális hurokdetektor.
 *
 * A korábbi batch algoritmus minden preview-frissítésnél az egész addigi
 * aktivitást újraszámolta. Ez kis útvonalon is egyre drágább lett, főleg egy
 * már bejárt fal mellett, ahol minden új cella több korábbi kontaktból képezhet
 * hurokjelöltet.
 *
 * Ez az osztály pontosan ugyanazt a döntési logikát használja, de egy új H3
 * cella érkezésekor CSAK az új cella által létrehozott jelölteket vizsgálja.
 * A `detectLoopsDetailed()` lent kompatibilis batch wrapper marad.
 */
export class IncrementalLoopDetector {
  private readonly path: CellId[] = [];
  private readonly loops: DetectedLoop[] = [];
  private readonly successful: LoopDiagnostics['successful'] = [];
  private readonly rejected: RejectedLoopDiagnostic[] = [];
  private shortRevisits = 0;

  private readonly seenAt = new Map<CellId, number[]>();
  private readonly accepted: AcceptedLoopRecord[] = [];
  private closureBlock: ClosureBlock | null = null;

  /**
   * Minden eddig bekerített cella (fal + belső).
   *
   * Ahhoz kell, hogy egy KORÁBBI BEZÁRÁS FÖLÉ visszanyúló jelölt csak akkor
   * számítson új bezárásnak, ha valódi új területet is hoz. Enélkül egy
   * háromnégyzetes útvonalon megjelent egy negyedik, `belső 503` méretű hurok,
   * ami pontosan az első két hurok uniója volt — nulla új cellával.
   */
  private readonly enclosed = new Set<CellId>();

  /** Hány H3 cellát dolgoztunk már fel. */
  get length(): number {
    return this.path.length;
  }

  /**
   * Egy új, már összefüggő cellalánc-elemet dolgoz fel.
   * Visszatérési érték: keletkezett-e ezen a lépésen új elfogadott hurok.
   */
  append(cell: CellId): boolean {
    const i = this.path.length;
    this.path.push(cell);

    const sameHistory = this.seenAt.get(cell) ?? [];
    const latestSame = sameHistory[sameHistory.length - 1];

    if (latestSame !== undefined && i - latestSame < GAMEPLAY.MIN_LOOP_STEPS) {
      this.shortRevisits += 1;
    }

    if (this.closureBlock !== null) {
      const minimumRepeatSteps = Math.max(
        GAMEPLAY.MIN_LOOP_STEPS,
        Math.floor(this.closureBlock.loop.wall.size * 0.75),
      );
      const repeatReady = i - this.closureBlock.acceptedAt >= minimumRepeatSteps;
      const inClosureZone = insideClosureZone(cell, this.closureBlock.loop);

      /**
       * Egy teljesen új traversal NEM kötődhet az előző closure eredeti
       * kapucellájához. Ugyanazt a fizikai területet ellenkező irányból, GPS-
       * jitterrel vagy egy cellával kijjebb bejárva természetesen más ponton
       * érinthetjük meg először.
       *
       * Korábban csak `gate.has(cell)` oldotta fel a blokkot. Emiatt ugyanaz a
       * nagy külső kör egyik irányban bezárt, a másikban viszont a végső
       * kontaktot a closure zone egyszerűen lenyelte.
       *
       * Ha már közel egy teljes új falhossznyi traversal elkészült, a lezárt
       * régió BÁRMELY valódi kontaktja új closure-jelölt lehet. A normál
       * candidate + sliver + traversal-duplicate szűrők továbbra is eldöntik,
       * hogy ebből ténylegesen érvényes új hurok lesz-e.
       */
      if (repeatReady && inClosureZone) {
        this.closureBlock = null;
        // Szándékosan NEM return: a mostani kontakt maga lehet az új hurok
        // bezárási pontja, ezért ezen a cellán már lefuttatjuk a jelöltkeresést.
      } else if (inClosureZone) {
        noteVisit(this.seenAt, cell, i);
        return false;
      } else {
        // Az első valóban külső cella szeparátor. Ezen még nem keresünk új
        // hurkot; a következő cellától indulhat új closure-epizód.
        this.closureBlock = null;
        noteVisit(this.seenAt, cell, i);
        return false;
      }
    }

    /**
     * A mostani cella saját korábbi előfordulásai és a hat élszomszéd korábbi
     * előfordulásai lehetnek kapuk. Cellánként legfeljebb a három legfrissebb
     * strukturálisan érvényes előzményt tartjuk meg.
     */
    const candidates = new Set<number>();
    addRecentEligible(sameHistory, i, candidates);
    for (const near of ringOf(cell)) {
      if (near === cell) continue;
      addRecentEligible(this.seenAt.get(near) ?? [], i, candidates);
    }

    /**
     * Egy H3 kereszteződés több egymás melletti korábbi cellát érinthet. Ezeket
     * egyetlen kontaktfolttá vonjuk össze, és a korábbi viselkedéssel egyezően
     * a legfrissebb indexet próbáljuk. Külön korábbi áthaladások a nagyobb
     * indexkülönbség miatt külön jelöltek maradnak.
     */
    const candidateIndices = clusterCandidateIndices(candidates);
    const lastClosureAt = this.accepted[this.accepted.length - 1]?.toIndex ?? -1;

    for (const previous of candidateIndices) {
      const rawWall = new Set(this.path.slice(previous, i + 1));
      const wall = pruneDeadEnds(rawWall);
      const prunedCells = Math.max(0, rawWall.size - wall.size);

      /**
       * Egy nagy hurok (pl. egy Balaton-méretű kör) ismételt bejárása gyakran
       * BIT-AZONOS falat termel az előzővel — ugyanaz a nyomvonal-szakasz,
       * ugyanazokkal a H3 cellákkal. Ilyenkor `buildLoopInterior()` kimenete
       * garantáltan megegyezik a korábbival (tiszta függvény a falon), tehát a
       * drága flood fill / compact feltárás újrafuttatása nélkül is
       * felhasználható. Ez NEM közelítés: csak pontos halmazegyezés esetén
       * talál találatot.
       *
       * Enélkül minden ismétlés a teljes O(terület) költséget újra kifizeti —
       * mérve: egy 6,3 km-es kör 4. ismétlése a másodperces tartományba nőtt.
       */
      const reused = findExactWallMatch(wall, previous, this.accepted);

      /**
       * A „nyeles kör” hazaútja: a jelölt fala egy nagy, már elfogadott kör +
       * az út két oldala. A teljes úton ez mindig duplikátumként esik ki — itt
       * ugyanezt bizonyítjuk a drága belső-felépítés nélkül. Mérve (GRUNDO #60,
       * `7d243470`): 615 ilyen jelölt, 167 s a 179-ből.
       */
      if (!reused && isProvenContainedDuplicate(wall, previous, this.accepted)) continue;

      let interiorGeometry: ReturnType<typeof buildLoopInterior>;
      if (reused) {
        interiorGeometry = {
          interior: reused.interior,
          ...(reused.compactInterior ? { compactInterior: reused.compactInterior } : {}),
          cellCount: loopInteriorCellCount(reused),
        };
      } else {
        try {
          interiorGeometry = buildLoopInterior(wall);
        } catch (err) {
          if (err instanceof LoopTooLargeError) {
            this.rejected.push({
              reason: 'too_large',
              fromIndex: previous,
              toIndex: i,
              wallCells: wall.size,
              interiorCells: 0,
              prunedCells,
              candidateCells: err.candidateCells,
            });
            continue;
          }
          throw err;
        }
      }

      if (interiorGeometry.cellCount < GAMEPLAY.MIN_INTERIOR_CELLS) {
        this.rejected.push({
          reason: 'interior_too_small',
          fromIndex: previous,
          toIndex: i,
          wallCells: wall.size,
          interiorCells: interiorGeometry.cellCount,
          prunedCells,
        });
        continue;
      }

      let candidate: DetectedLoop = {
        wall,
        interior: interiorGeometry.interior,
        ...(interiorGeometry.compactInterior
          ? { compactInterior: interiorGeometry.compactInterior }
          : {}),
        fromIndex: previous,
        toIndex: i,
      };

      /**
       * Ha egy már lezárt fal mellől induló későbbi nyom csak egy egysoros,
       * két dimenziós mag nélküli slivert csíp le, az nem új játékterület.
       *
       * Tipikus eset: bezártuk a négyzetet, majd a régi fal mellett kifelé
       * haladunk. A H3 szomszédság 1-2 belső cellás mesterséges „hurkot” látna,
       * pedig ez csak a cellakvantálásból adódó kilógó érintés. Valódi új lobe,
       * amelyik a régi falat használja egyik oldalként, továbbra is számít,
       * amint van tényleges 2D belső magja.
       */
      if (isThinPostClosureSliver(candidate, previous, this.accepted)) {
        continue;
      }

      // Ugyanannak a geometriai huroknak a közvetlen folytatása nem kap új
      // bezárást. Egy teljesen új traversal viszont továbbra is számíthat.
      if (isTraversalDuplicate(candidate, previous, this.accepted)) {
        continue;
      }

      /**
       * EGY KORÁBBI BEZÁRÁS FÖLÉ VISSZANYÚLVA ÚJ TERÜLET KELL.
       *
       * Ha a jelölt kapuja a legutóbbi bezárás ELŐTTRŐL való, akkor a fala már
       * lezárt szakaszokat fűz össze. Ez lehet jogos — a 03. fejezet engedi,
       * hogy a saját terület széle egy új hurok fala legyen —, de csak akkor,
       * ha közben valóban bekerítettünk valami újat.
       *
       * Ami NEM esik ide: az ismételt teljes kör. Ott a kapu a legutóbbi bezárás
       * UTÁNRÓL való (a lapok egymás után futnak), tehát a szabály nem szól bele,
       * és az ötször megfutott kör továbbra is öt bezárást ad.
       */
      if (previous < lastClosureAt && !addsNewGround(candidate, this.accepted, this.enclosed)) {
        this.rejected.push({
          reason: 'interior_too_small',
          fromIndex: previous,
          toIndex: i,
          wallCells: candidate.wall.size,
          interiorCells: loopInteriorCellCount(candidate),
          prunedCells,
        });
        continue;
      }

      // Tényleges új lapnál a bezárás megmarad, de egy 1-2 cellás H3-kapu
      // jitter ne növelje lassan a területet minden körrel.
      candidate = canonicalizeNearIdenticalRepeat(candidate, previous, this.accepted);

      this.loops.push(candidate);
      this.successful.push({
        fromIndex: previous,
        toIndex: i,
        wallCells: candidate.wall.size,
        interiorCells: loopInteriorCellCount(candidate),
        prunedCells,
      });
      this.accepted.push({ loop: candidate, toIndex: i });
      for (const wallCell of candidate.wall) this.enclosed.add(wallCell);
      if (!candidate.compactInterior) {
        for (const inner of candidate.interior) this.enclosed.add(inner);
      }
      this.closureBlock = {
        loop: candidate,
        acceptedAt: i,
      };
      noteVisit(this.seenAt, cell, i);
      return true;
    }

    noteVisit(this.seenAt, cell, i);
    return false;
  }

  appendMany(cells: readonly CellId[]): number {
    let addedLoops = 0;
    for (const cell of cells) {
      if (this.append(cell)) addedLoops += 1;
    }
    return addedLoops;
  }

  snapshot(): IncrementalLoopSnapshot {
    return {
      loops: [...this.loops],
      diagnostics: {
        successful: [...this.successful],
        rejected: [...this.rejected],
        shortRevisits: this.shortRevisits,
      },
    };
  }
}

/**
 * Hurokdetektor átfedő és egymásba kapcsolódó bezárásokhoz.
 *
 * A H3-on egy fizikai útvonal-találkozás nem egy matematikai pont, hanem több
 * cellából álló kontaktfolt. Ráadásul egy már bezárt régió peremén/belsejében
 * továbbhaladva ugyanabból az útvonalból több, egyre nagyobb kompozit ciklus is
 * levezethető. Ezek gráfelméletileg létező ciklusok, de a játék szempontjából
 * NEM külön bezárási események.
 *
 * A batch API kompatibilitásból megmarad, de belül ugyanazt az inkrementális
 * állapotgépet eteti végig pontosan egyszer.
 */
export function detectLoopsDetailed(path: readonly CellId[]): {
  loops: DetectedLoop[];
  diagnostics: LoopDiagnostics;
} {
  const detector = new IncrementalLoopDetector();
  detector.appendMany(path);
  return detector.snapshot();
}

export function detectLoops(path: readonly CellId[]): DetectedLoop[] {
  return detectLoopsDetailed(path).loops;
}

function noteVisit(history: Map<CellId, number[]>, cell: CellId, index: number): void {
  const visits = history.get(cell);
  if (visits) visits.push(index);
  else history.set(cell, [index]);
}

function addRecentEligible(
  history: readonly number[],
  currentIndex: number,
  target: Set<number>,
): void {
  let added = 0;
  for (let i = history.length - 1; i >= 0 && added < 3; i -= 1) {
    const index = history[i]!;
    if (currentIndex - index < GAMEPLAY.MIN_LOOP_STEPS) continue;
    target.add(index);
    added += 1;
  }
}

/**
 * Ugyanahhoz a fizikai H3 kontaktfolthoz tartozó path-indexeket összevonja.
 * A lista csökkenő sorrendű, minden klaszterből a legfrissebb index marad.
 */
function clusterCandidateIndices(candidates: ReadonlySet<number>): number[] {
  const sorted = [...candidates].sort((a, b) => b - a);
  if (sorted.length < 2) return sorted;

  const result: number[] = [];
  let clusterNewest = sorted[0]!;
  let previous = sorted[0]!;

  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i]!;
    if (previous - current <= CONTACT_INDEX_CLUSTER_GAP) {
      previous = current;
      continue;
    }
    result.push(clusterNewest);
    clusterNewest = current;
    previous = current;
  }
  result.push(clusterNewest);
  return result;
}

/**
 * A lezárt régió maga + a fal közvetlen külső kontaktzónája.
 *
 * Compact nagy huroknál a homogén belsőt parent-membership alapján kérdezzük,
 * tehát itt sem kell több millió res12 cellát materializálni.
 */
function insideClosureZone(cell: CellId, loop: DetectedLoop): boolean {
  if (loop.wall.has(cell) || loopInteriorHas(loop, cell)) return true;
  for (const near of ringOf(cell)) {
    if (loop.wall.has(near)) return true;
  }
  return false;
}

/**
 * Egy régi, már lezárt falhoz tapadó egysoros H3-sliver felismerése.
 * Csak akkor aktív, ha a jelölt ténylegesen egy korábbi closure előtti falindexet
 * használ újra; önálló első kis hurokra ezért nincs hatással.
 */
function isThinPostClosureSliver(
  candidate: DetectedLoop,
  previous: number,
  accepted: readonly AcceptedLoopRecord[],
): boolean {
  if (candidate.compactInterior || hasTwoDimensionalInteriorCore(candidate)) return false;

  for (let i = accepted.length - 1; i >= 0; i -= 1) {
    const record = accepted[i]!;
    if (record.toIndex <= previous) break;
    if (record.toIndex >= candidate.toIndex) continue;
    if (wallsOverlap(candidate.wall, record.loop.wall)) return true;
  }
  return false;
}

/**
 * A lineáris/egysoros belső minden cellájának legfeljebb két belső szomszédja
 * van. Valódi 2D magban legalább egy cella három vagy több belső oldalszomszédot
 * kap. Ez felbontásfüggetlenebb jel, mint egy önkényes „min. N belső cella”.
 */
function hasTwoDimensionalInteriorCore(loop: DetectedLoop): boolean {
  if (loop.compactInterior) return true;
  for (const cell of loop.interior) {
    let neighbours = 0;
    for (const near of ringOf(cell)) {
      if (near === cell) continue;
      if (loopInteriorHas(loop, near)) neighbours += 1;
      if (neighbours >= 3) return true;
    }
  }
  return false;
}

/**
 * Egy korábban már elfogadott hurok, aminek a fala BIT-AZONOS a most vizsgált
 * jelölt falával — csak ekkor biztonságos a drága `buildLoopInterior()`
 * kihagyása, mert a kimenete a falon tiszta függvény.
 *
 * Csak korábbi bezárásokat nézünk (`record.toIndex <= previous`), ugyanúgy,
 * ahogy a `canonicalizeNearIdenticalRepeat` is teszi — egy jövőbeli bezárás
 * fala még nem létezhet.
 */
function findExactWallMatch(
  wall: ReadonlySet<CellId>,
  previous: number,
  accepted: readonly AcceptedLoopRecord[],
): DetectedLoop | undefined {
  for (let i = accepted.length - 1; i >= 0; i -= 1) {
    const record = accepted[i]!;
    if (previous < record.toIndex) continue;
    if (record.loop.wall.size !== wall.size) continue;
    if (setsEqual(wall, record.loop.wall)) return record.loop;
  }
  return undefined;
}

function setsEqual(a: ReadonlySet<CellId>, b: ReadonlySet<CellId>): boolean {
  if (a.size !== b.size) return false;
  for (const cell of a) {
    if (!b.has(cell)) return false;
  }
  return true;
}

function wallsOverlap(a: ReadonlySet<CellId>, b: ReadonlySet<CellId>): boolean {
  const smaller = a.size <= b.size ? a : b;
  const larger = smaller === a ? b : a;
  for (const cell of smaller) {
    if (larger.has(cell)) return true;
  }
  return false;
}

/**
 * Hoz-e a jelölt olyan belső cellát, amit még egyetlen bezárás sem kerített be.
 *
 * A küszöb ugyanaz a `MIN_INTERIOR_CELLS`, amivel a detektor eleve eldönti, hogy
 * egy hurok érdemi-e — nincs új, önkényes szám a rendszerben.
 *
 * Compact huroknál a belsőt nem bontjuk ki res12-re, ezért ott csak annyit
 * kérdezünk, hogy nem ugyanazt a geometriát látjuk-e újra.
 */
function addsNewGround(
  candidate: DetectedLoop,
  accepted: readonly AcceptedLoopRecord[],
  enclosed: ReadonlySet<CellId>,
): boolean {
  if (candidate.compactInterior) {
    return !accepted.some((record) => repeatsWithoutNewGround(candidate, record.loop));
  }
  let fresh = 0;
  for (const cell of candidate.interior) {
    if (enclosed.has(cell)) continue;
    fresh += 1;
    if (fresh >= GAMEPLAY.MIN_INTERIOR_CELLS) return true;
  }
  return false;
}

/**
 * Ugyanaz a döntés, mint amit a teljes belső felépítése után az
 * `isTraversalDuplicate()` hozna — de a belső felépítése NÉLKÜL, ha a jelölt
 * egy nagy, már elfogadott hurok falát tartalmazza, és bizonyíthatóan nem
 * kerít be új zsebet (`shrunkInteriorCellCount`).
 *
 * Csak az IGAZ választ adja bizonyítottan. `false` = „nem tudjuk”, ekkor a hívó
 * a teljes utat futtatja, tehát az eredmény sosem térhet el a korábbitól.
 *
 * Miért pontos a sorrend is: a teljes úton a duplikátum-szűrő előtt csak a
 * `too_large` (a bizonyítás kizárja), az `interior_too_small` (itt kizárva) és
 * a sliver-szűrő fut, ami compact jelöltre mindig hamis.
 */
function isProvenContainedDuplicate(
  wall: ReadonlySet<CellId>,
  previous: number,
  accepted: readonly AcceptedLoopRecord[],
): boolean {
  for (let i = accepted.length - 1; i >= 0; i -= 1) {
    const record = accepted[i]!;
    if (previous >= record.toIndex) break;
    if (!record.loop.compactInterior) continue;

    const shrunk = shrunkInteriorCellCount(record.loop, wall);
    if (shrunk === null || shrunk.cellCount < GAMEPLAY.MIN_INTERIOR_CELLS) continue;

    const { cellCount, sharedWithBase, newGround } = shrunk;
    if (
      sameGeometryByCounts(
        cellCount,
        loopInteriorCellCount(record.loop),
        () => sharedWithBase,
        wall,
        record.loop.wall,
      ) &&
      // Ugyanaz a kivétel, mint a teljes úton (`repeatsWithoutNewGround`).
      !hasSolidCore(new Set(newGround))
    ) {
      return true;
    }
  }
  return false;
}

/**
 * „Ugyanaz a hurok, új terület nélkül” — ez a duplikátum.
 *
 * ⚠️ A HASONLÓSÁG ÖNMAGÁBAN NEM ELÉG (GRUNDO #60, Geri 100 km-es köre): a
 * nagy kör után egy MÁSIK úton hazatérve a visszaút a rajt közelében összeért
 * az induló szakasszal, és a kettő között 2,74 km² valódi új terület zárult
 * be. A jelölt belseje ettől még 98 %-ban a nagy kör volt, tehát a puszta
 * hasonlóság duplikátumnak vette — nem jött felugró üzenet, és a terület
 * elveszett. Ha a jelölt VALÓDI új területet is bekerít (`hasSolidCore`), az
 * bezárás; a foglalás a régi részen úgysem ad újat, csak az új rész hozzáadódik.
 */
function repeatsWithoutNewGround(candidate: DetectedLoop, record: DetectedLoop): boolean {
  return (
    sameLoopGeometry(candidate, record) && !hasSolidCore(newGroundOutside(candidate, record))
  );
}

function isTraversalDuplicate(
  candidate: DetectedLoop,
  previous: number,
  accepted: readonly AcceptedLoopRecord[],
): boolean {
  for (let i = accepted.length - 1; i >= 0; i -= 1) {
    const record = accepted[i]!;
    if (previous >= record.toIndex) break;
    if (repeatsWithoutNewGround(candidate, record.loop)) return true;
  }
  return false;
}

/**
 * Tényleges új traversalnál egy majdnem bitazonos H3-hurok cellahalmazát
 * kanonizáljuk az előzőre. A bezárás ettől megmarad (defense credit jár), csak
 * az 1-2 cellás kapuzási jitter nem hoz létre új területet.
 */
function canonicalizeNearIdenticalRepeat(
  candidate: DetectedLoop,
  previous: number,
  accepted: readonly AcceptedLoopRecord[],
): DetectedLoop {
  if (candidate.compactInterior) return candidate;

  for (let i = accepted.length - 1; i >= 0; i -= 1) {
    const record = accepted[i]!;
    if (previous < record.toIndex) continue;
    if (record.loop.compactInterior) continue;
    if (!sameLoopGeometry(candidate, record.loop)) continue;
    if (normalLoopSymmetricDifference(candidate, record.loop) > REPEAT_CANONICAL_MAX_SYMMETRIC_DIFF) {
      continue;
    }

    return {
      wall: new Set(record.loop.wall),
      interior: new Set(record.loop.interior),
      fromIndex: candidate.fromIndex,
      toIndex: candidate.toIndex,
    };
  }
  return candidate;
}

function normalLoopSymmetricDifference(a: DetectedLoop, b: DetectedLoop): number {
  const aCells = new Set<CellId>([...a.wall, ...a.interior]);
  const bCells = new Set<CellId>([...b.wall, ...b.interior]);
  let difference = 0;
  for (const cell of aCells) if (!bCells.has(cell)) difference += 1;
  for (const cell of bCells) if (!aCells.has(cell)) difference += 1;
  return difference;
}

/**
 * Ugyanazt a területet elsődlegesen a BELSEJE alapján azonosítjuk. Compact
 * geometriánál a metszetszám parent-szinten számolható, ezért egy Balaton-méretű
 * hurok deduplikációja sem bont vissza több millió res12 cellára.
 */
function sameLoopGeometry(a: DetectedLoop, b: DetectedLoop): boolean {
  return sameGeometryByCounts(
    loopInteriorCellCount(a),
    loopInteriorCellCount(b),
    () => loopInteriorOverlapCount(a, b),
    a.wall,
    b.wall,
  );
}

/**
 * A `sameLoopGeometry()` döntése számokból. Külön, hogy a felépítés nélküli
 * duplikátum-bizonyítás (`isProvenContainedDuplicate`) BETŰRE ugyanazt a
 * szabályt alkalmazza, ne egy másolatát.
 */
function sameGeometryByCounts(
  aInterior: number,
  bInterior: number,
  sharedInterior: () => number,
  aWall: ReadonlySet<CellId>,
  bWall: ReadonlySet<CellId>,
): boolean {
  const minInterior = Math.min(aInterior, bInterior);
  const maxInterior = Math.max(aInterior, bInterior);

  if (minInterior > 0) {
    if (minInterior / maxInterior < 0.85) return false;
    const shared = sharedInterior();
    if (shared / minInterior >= 0.95) return true;
  }

  const minWall = Math.min(aWall.size, bWall.size);
  const maxWall = Math.max(aWall.size, bWall.size);
  if (minWall === 0 || minWall / maxWall < 0.9) return false;

  const smallerWall = aWall.size <= bWall.size ? aWall : bWall;
  const largerWall = smallerWall === aWall ? bWall : aWall;
  let sharedWall = 0;
  for (const cell of smallerWall) {
    if (largerWall.has(cell)) sharedWall += 1;
  }
  return sharedWall / minWall >= 0.95;
}